<template>
  <section
    id="schedule"
    data-qa="lpu_doctors_schedule"
    class="lpu-doctors-schedule d-flex flex-column"
    :class="isMobile ? 'ga-5' : 'ga-8'"
  >
    <h2
      data-qa="lpu_doctors_schedule_title"
      class="text--text"
      :class="isMobile ? 'text-h6' : 'text-h5'"
    >
      {{ scheduleTitle }}
    </h2>

    <div
      class="d-flex"
      :class="isMobile ? 'flex-column ga-4' : 'flex-row ga-8 align-start'"
    >
      <div
        class="flex-grow-1 flex-shrink-1 min-w-0"
        :class="{ 'lpu-doctors-schedule__specialty--desktop': !isMobile }"
      >
        <v-skeleton-loader
          v-if="isSpecialitiesLoading"
          type="button"
          class="lpu-doctors-schedule__skeleton-select"
        />
        <SpecialtySelect
          v-else
          :specialities="specialities"
          :selected-speciality="selectedSpeciality"
          :disabled="isFailed || isSlotsLoading"
          @select="selectSpeciality"
        />
      </div>

      <div
        class="flex-grow-1 flex-shrink-1 d-flex flex-column ga-4 min-w-0 lpu-doctors-schedule__calendar"
        :class="{ 'lpu-doctors-schedule__calendar--desktop': !isMobile }"
        :style="{ minHeight: `${SCHEDULE_CALENDAR_MIN_HEIGHT_PX}px` }"
      >
        <LpuNotWorkingLabel
          v-if="context.nearest_appointment_express_datetime && isExpressAppointment"
          :lpu-working-text="lpuNotWorkingText"
        />

        <template v-if="isLoading">
          <ScheduleCalendarSkeleton />
        </template>

        <template v-else-if="isFailed || isEmptySchedule">
          <div class="d-flex flex-column ga-4 align-start">
            <div class="text-body-1 text-secondary--text">
              {{ emptyStateMessage }}
            </div>
            <div
              v-if="showCallButton"
              class="text-body-1 text-secondary--text"
            >
              {{ tStatic('Подробности уточняйте по телефону') }} {{ lpuPhoneFormatted }}
            </div>
            <v-btn
              v-if="showCallButton"
              data-qa="appointment-button-lpu-call"
              data-google-goal="appointment_button_lpu_call"
              class="branding-btn--primary text-none"
              color="primary"
              depressed
              :href="`tel:${lpuPhoneHref}`"
              :block="isMobile"
              @click="onCallButtonClick"
            >
              <span class="ui-icon-phone-none bg-gray-0--text mr-2" />
              {{ tStatic('Позвонить в клинику') }}
            </v-btn>
            <v-btn
              v-if="!isExpressAppointment"
              data-qa="lpu_doctors_schedule_doctors_cta"
              class="branding-btn--primary text-none"
              color="primary"
              depressed
              :href="`${lpuDoctorsUrl}#tab-content`"
            >
              {{ tStatic('Запись на приём') }}
            </v-btn>
          </div>
        </template>

        <template v-else>
          <DayTabs
            data-qa="lpu_doctors_schedule_days"
            :items="dayItems"
            :active-date="activeDate || undefined"
            @select="selectDay"
          />
          <SlotButtons
            v-if="activeDaySlots.length"
            data-qa="lpu_doctors_schedule_slots"
            slot-qa="lpu_doctors_schedule_slot"
            expand-qa="lpu_doctors_schedule_slots_expand"
            :slots="activeDaySlots"
            :is-disabled="isExpressAppointment"
            @click="onSlotClick"
          />
          <v-btn
            v-if="showCallButton"
            data-qa="appointment-button-lpu-call"
            data-google-goal="appointment_button_lpu_call"
            class="branding-btn--primary text-none"
            color="primary"
            depressed
            :href="`tel:${lpuPhoneHref}`"
            :block="isMobile"
            @click="onCallButtonClick"
          >
            <span class="ui-icon-phone-none bg-gray-0--text mr-2" />
            {{ tStatic('Позвонить в клинику') }}
          </v-btn>
        </template>

        <div
          v-if="isStaffUser && !isMobile"
          class="staff-appointment-type lpu-doctors-schedule__staff-appointment-type"
        >
          <div class="staff-appointment-type__text">
            {{ selectedSpeciality?.staff_appointment_type || '' }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import { format, isValid, parseISO } from 'date-fns'
import useURLStore from 'src/store/useURLStore'
import useContextStore from 'src/store/useContextStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import formatPhone from 'src/functions/formatPhone'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import useLpuDoctorsSchedule from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/composables/useLpuDoctorsSchedule'
import SpecialtySelect from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/components/SpecialtySelect.vue'
import DayTabs from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/DayTabs.vue'
import SlotButtons from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/SlotButtons.vue'
import ScheduleCalendarSkeleton from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/ScheduleCalendarSkeleton.vue'
import { SCHEDULE_CALENDAR_MIN_HEIGHT_PX } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/constants/scheduleCalendarMinHeight'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import LpuNotWorkingLabel from 'src/components/common/LpuNotWorkingLabel/LpuNotWorkingLabel.vue'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import type { LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context, urlLang }: ContextStore = useContextStore()
const { reverseFullL10N } = useURLStore()

const lpuDoctorsUrl = reverseFullL10N({
  name: 'lpu_doctors',
  args: {
    language: urlLang,
    town_translit: props.lpu.town.translit,
    lpu_translit: props.lpu.translit,
  },
})

const scheduleTitle = computed(() => tStatic(
  props.lpu.lputype.translit === 'wellness'
    ? 'График работы специалистов'
    : 'График работы врачей',
))
const initialAppointmentType = computed<'direct' | 'express' | null>(() => {
  const appointmentType = unwrapList<LpuDoctor>(context.doctors)[0]?.workplace_info?.appointment_type

  if (appointmentType === 'direct' || appointmentType === 'express') {
    return appointmentType
  }

  return null
})
const isStaffUser = computed(() => Boolean(context.request.user.is_staff))
const lpuPhone = computed(() => props.lpu.lpuphone?.phone || '')
const lpuPhoneHref = computed(() => {
  if (!lpuPhone.value) {
    return ''
  }

  return formatPhone({
    phone: lpuPhone.value,
    country: { iso: context.request.country.iso.toLowerCase() },
    isRaw: true,
  })
})
const lpuPhoneFormatted = computed(() => (
  lpuPhone.value
    ? formatPhone({
      phone: lpuPhone.value,
      country: { iso: context.request.country.iso.toLowerCase() },
    })
    : ''
))
const lpuNotWorkingText = computed(() => {
  const expressDateTime = context.nearest_appointment_express_datetime

  if (!expressDateTime) {
    return ''
  }

  const parsedDate = parseISO(expressDateTime)

  if (!isValid(parsedDate)) {
    return ''
  }

  const locale = getDateFnsLocaleByIso(context.current_language.iso)

  return `${tStatic('Клиника закрыта, откроется')} ${format(parsedDate, 'd MMMM', { locale })} ${tStatic('в')} ${format(parsedDate, 'HH:mm')}.`
})

const {
  specialities,
  selectedSpeciality,
  dayItems,
  activeDate,
  activeDaySlots,
  appointmentType,
  isLoading,
  isEmptySchedule,
  isSpecialitiesLoading,
  isSlotsLoading,
  isFailed,
  selectSpeciality,
  selectDay,
  onSlotClick,
} = useLpuDoctorsSchedule(
  toRef(props, 'lpu'),
  initialAppointmentType,
)

const effectiveAppointmentType = computed(() => appointmentType.value || initialAppointmentType.value)
const isExpressAppointment = computed(() => effectiveAppointmentType.value === 'express')
const isPhoneAppointmentEnabled = computed(() => Boolean(lpuPhone.value))
const isLpuWorking = computed(() => !context.nearest_appointment_express_datetime)
const showCallButton = computed(() => (
  isMobile.value
  && isExpressAppointment.value
  && isPhoneAppointmentEnabled.value
  && isLpuWorking.value
))
const emptyStateMessage = computed(() => (
  isExpressAppointment.value && isPhoneAppointmentEnabled.value
    ? tStatic('Свободной записи по этой специальности нет')
    : tStatic('Расписание недоступно')
))

function onCallButtonClick() {
  if (typeof window === 'undefined') {
    return
  }

  if (typeof window.ym === 'function' && window.YA_COUNTER_ID) {
    window.ym(window.YA_COUNTER_ID, 'reachGoal', 'appointment-call__button')
  }
}
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-doctors-schedule {
  &__specialty--desktop,
  &__calendar--desktop {
    flex: 1 1 50%;
    max-width: 50%;
    min-width: 0;
  }

  &__skeleton-select {
    height: 48px;
    border-radius: $border-radius-sm;
    overflow: hidden;
  }

  &__staff-appointment-type {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}
</style>
